import {IGetGroundTruthEvaluationResponse} from '../api/types/aiReview.type';

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asArray = (value: unknown): unknown[] | null => {
  if (Array.isArray(value)) return value;
  const record = toRecord(value);
  if (record && Array.isArray(record.$values)) {
    return record.$values;
  }
  return null;
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const pickNonEmptyString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

export const getGroundTruthMatrixRow = (
  value: unknown
): Record<string, unknown> => {
  const rows = asArray(value);
  if (rows?.length) {
    return toRecord(rows[0]) ?? {};
  }
  return toRecord(value) ?? {};
};

export const mapGroundTruthConfusionMatrix = (
  value: unknown
): IGetGroundTruthEvaluationResponse['confusionMatrix'] => {
  const matrix = toRecord(value) ?? {};
  return {
    truePositives: toFiniteNumber(
      matrix.truePositives ?? matrix.TruePositives
    ),
    falsePositives: toFiniteNumber(
      matrix.falsePositives ?? matrix.FalsePositives
    ),
    trueNegatives: toFiniteNumber(
      matrix.trueNegatives ?? matrix.TrueNegatives
    ),
    falseNegatives: toFiniteNumber(
      matrix.falseNegatives ?? matrix.FalseNegatives
    ),
  };
};

export const mapGroundTruthMetrics = (
  value: unknown
): IGetGroundTruthEvaluationResponse['metrics'] => {
  const metrics = toRecord(value) ?? {};
  return {
    accuracy: toFiniteNumber(metrics.accuracy ?? metrics.Accuracy),
    precision: toFiniteNumber(metrics.precision ?? metrics.Precision),
    recall: toFiniteNumber(metrics.recall ?? metrics.Recall),
    f1Score: toFiniteNumber(
      metrics.f1Score ?? metrics.F1Score ?? metrics.f1score
    ),
  };
};

export const mapGroundTruthEvaluation = (
  data: unknown,
  jobId: string | number
): IGetGroundTruthEvaluationResponse => {
  const payload = toRecord(data) ?? {};
  const payloadJobId = payload.jobId ?? payload.JobId;
  const matrixRow = getGroundTruthMatrixRow(
    payload.confusionMatrix ?? payload.ConfusionMatrix
  );
  const nestedMetrics = toRecord(payload.metrics ?? payload.Metrics);

  return {
    jobId: payloadJobId == null ? jobId : (payloadJobId as string | number),
    groundTruthName: pickNonEmptyString(
      payload.groundTruthName,
      payload.GroundTruthName
    ),
    groundTruthDocCount: toFiniteNumber(
      payload.groundTruthDocCount ?? payload.GroundTruthDocCount
    ),
    confusionMatrix: mapGroundTruthConfusionMatrix(matrixRow),
    metrics: mapGroundTruthMetrics({
      ...(nestedMetrics ?? {}),
      ...matrixRow,
    }),
  };
};
